import _ from 'lodash';

export const findChangesInObject = (oldObject, newObject, compareOnlyExistingProps = false, normaliseKey = false) => {
    const diffs = {};
    const ignoredProperties = ['createdAt', 'updatedAt', 'createdBy', 'updatedBy'];

    ignoredProperties.forEach(prop => {
        if (Object.prototype.hasOwnProperty.call(oldObject, prop)) delete oldObject[prop];
        if (Object.prototype.hasOwnProperty.call(newObject, prop)) delete newObject[prop];
    });

    // True if a value is an array of {label/name, value} attribute objects.
    // Even one item having a label/name key is enough to treat the whole array
    // as a labeled attribute collection (the other side may be empty/null).
    const isLabeledAttrArray = (val) =>
        Array.isArray(val) &&
        val.some(item => item && typeof item === 'object' && ('label' in item || 'name' in item));

    // Index a labeled attribute array by its label into { [label]: value }.
    const attrArrayToMap = (arr) => {
        const map = {};
        if (!Array.isArray(arr)) return map;
        for (const item of arr) {
            const label = item?.label ?? item?.name;
            if (label != null) map[String(label)] = item?.value ?? null;
        }
        return map;
    };

    Object.entries(newObject).forEach(([key, value]) => {        
        if (normaliseKey) key = key.charAt(0).toLowerCase() + key.slice(1);

        if (compareOnlyExistingProps && !Object.prototype.hasOwnProperty.call(oldObject, key)) { // only compare properties that exist on the original object
            return;
        }

        const oldValue = oldObject[key];
        if ((Number.isNaN(oldValue) && Number.isNaN(value)) || (!oldValue && !value))  return;

        if (_.isEqual(oldValue, value)) return;

        // Labeled attribute arrays (e.g. typeCustomAttributes, customAttributes):
        // flatten by label → typeCustomAttributes_Site: { new: "London", old: "Birmingham" }
        if (isLabeledAttrArray(value) || isLabeledAttrArray(oldValue)) {
            const newMap = attrArrayToMap(value);
            const oldMap = attrArrayToMap(oldValue);
            const allLabels = new Set([...Object.keys(newMap), ...Object.keys(oldMap)]);
            allLabels.forEach(label => {
                const nv = newMap[label] ?? null;
                const ov = oldMap[label] ?? null;
                if (!_.isEqual(nv, ov)) {
                    diffs[`${key}_${label}`] = { new: nv, old: ov };
                }
            });
            return;
        }

        // Plain objects (non-array): flatten one level →
        // address_city: { new: "London", old: "Birmingham" }
        if (value && typeof value === 'object' && !Array.isArray(value) &&
            oldValue && typeof oldValue === 'object' && !Array.isArray(oldValue)) {
            const allSubKeys = new Set([...Object.keys(oldValue), ...Object.keys(value)]);
            allSubKeys.forEach(subKey => {
                const nv = value[subKey] ?? null;
                const ov = oldValue[subKey] ?? null;
                if (!_.isEqual(nv, ov)) {
                    diffs[`${key}_${subKey}`] = { new: nv, old: ov };
                }
            });
            return;
        }

        // Primitives / arrays that aren't labeled attr collections: compare directly
        diffs[key] = { new: value, old: oldValue };
    });
    return diffs;
}

export const findChangesInModuleObject = (oldObject, newObject, moduleRevisionFormInputs) => {
    const diffs = {};

    if (oldObject && newObject && Array.isArray(oldObject.inputValues) && Array.isArray(newObject.inputValues)) {
        // index old values by inputGuid for fast lookup
        const oldMap = _.keyBy(oldObject.inputValues, "inputGuid");
        const newMap = _.keyBy(newObject.inputValues, "inputGuid");

        Object.keys(newMap).forEach((guid) => {
            if (oldMap[guid]) {
                const oldVal = oldMap[guid].value;
                const newVal = newMap[guid].value;

                let inputLabel = ""
                const formInput = moduleRevisionFormInputs.find(x => x.guid === guid)
                if (formInput) {
                    inputLabel = formInput.label
                }

                if (!_.isEqual(oldVal, newVal)) {
                    diffs[inputLabel] = { new: newVal, old: oldVal };
                }
            }
        });
    }
    return diffs;
};